/**
 * Product title line clamp — Shopify Dawn (and compatible themes)
 *
 * Desktop / tablet (>= 770px): 1 line + ellipsis when needed
 * Mobile (< 770px): 2 lines + ellipsis when needed
 *
 * Include in theme.liquid (before </body>):
 *   {{ 'product-title-clamp.js' | asset_url | script_tag }}
 */
(function productTitleClamp() {
  'use strict';

  const SELECTOR = '.single-line-link';
  const BREAKPOINT_PX = 770;
  const ELLIPSIS = '\u2026'; // …
  const RESIZE_DEBOUNCE_MS = 120;
  const SUBPIXEL_TOLERANCE = 1;

  /** @type {number | null} */
  let cachedMaxLines = null;
  /** @type {number} */
  let resizeTimer = 0;
  /** @type {number} */
  let mutateTimer = 0;
  /** @type {boolean} */
  let rafPending = false;
  /** @type {Set<Element>} */
  const pendingRoots = new Set();
  /** @type {MutationObserver | null} */
  let mutationObserver = null;

  /**
   * @returns {1 | 2}
   */
  function getMaxLines() {
    return window.innerWidth < BREAKPOINT_PX ? 2 : 1;
  }

  /**
   * @param {Element} el
   * @returns {string}
   */
  function readFullText(el) {
    const stored = el.dataset.fullText;
    if (stored) return stored;

    const fromDom = el.textContent.replace(/\u2026$/, '').trim();
    el.dataset.fullText = fromDom;
    return fromDom;
  }

  /**
   * @param {Element} el
   * @param {1 | 2} maxLines
   */
  function resetForMeasure(el, maxLines) {
    const full = readFullText(el);
    el.textContent = full;
    el.style.setProperty('--title-max-lines', String(maxLines));
    el.classList.remove('is-truncated');
    el.removeAttribute('title');
    return full;
  }

  /**
   * @param {Element} el
   * @returns {{ lineHeight: number, maxHeight: number }}
   */
  function getLineMetrics(el, maxLines) {
    const style = window.getComputedStyle(el);
    let lineHeight = parseFloat(style.lineHeight);

    if (!Number.isFinite(lineHeight)) {
      const fontSize = parseFloat(style.fontSize) || 16;
      lineHeight = fontSize * 1.4;
    }

    return {
      lineHeight,
      maxHeight: lineHeight * maxLines,
    };
  }

  /**
   * @param {Element} el
   * @param {string} full
   * @param {number} maxHeight
   * @returns {boolean} true when text fits without truncation
   */
  function fitsWithoutTruncation(el, full, maxHeight) {
    el.textContent = full;
    return el.scrollHeight <= maxHeight + SUBPIXEL_TOLERANCE;
  }

  /**
   * Binary search for the longest prefix that fits with ellipsis.
   *
   * @param {Element} el
   * @param {string} full
   * @param {number} maxHeight
   * @returns {string | null} truncated string with ellipsis, or null if no truncation
   */
  function findTruncatedText(el, full, maxHeight) {
    let low = 1;
    let high = full.length;
    /** @type {string | null} */
    let best = null;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = full.slice(0, mid) + ELLIPSIS;

      el.textContent = candidate;

      if (el.scrollHeight > maxHeight + SUBPIXEL_TOLERANCE) {
        high = mid - 1;
      } else {
        best = candidate;
        low = mid + 1;
      }
    }

    return best;
  }

  /**
   * @param {Element} el
   * @param {1 | 2} maxLines
   */
  function clampElement(el, maxLines) {
    const full = resetForMeasure(el, maxLines);
    const { maxHeight } = getLineMetrics(el, maxLines);

    if (fitsWithoutTruncation(el, full, maxHeight)) {
      return;
    }

    const truncated = findTruncatedText(el, full, maxHeight);

    if (truncated && truncated !== full) {
      el.textContent = truncated;
      el.classList.add('is-truncated');
      el.setAttribute('title', full);
    } else {
      el.textContent = full;
    }
  }

  /**
   * Collect clamp targets under a root (or the root itself).
   *
   * @param {ParentNode} root
   * @returns {Element[]}
   */
  function collectTargets(root) {
    /** @type {Element[]} */
    const targets = [];

    if (root instanceof Element && root.matches(SELECTOR)) {
      targets.push(root);
    }

    if (root instanceof Element || root instanceof Document || root instanceof DocumentFragment) {
      root.querySelectorAll(SELECTOR).forEach((el) => {
        if (!targets.includes(el)) targets.push(el);
      });
    }

    return targets;
  }

  /**
   * @param {ParentNode} [root]
   */
  function runClamp(root) {
    const scope = root || document;
    const maxLines = getMaxLines();
    const targets = collectTargets(scope);

    if (!targets.length) return;

    targets.forEach((el) => clampElement(el, maxLines));
  }

  function flushPending() {
    rafPending = false;
    cachedMaxLines = getMaxLines();

    if (pendingRoots.size === 0) {
      runClamp(document);
      return;
    }

    pendingRoots.forEach((root) => runClamp(root));
    pendingRoots.clear();
  }

  /**
   * @param {ParentNode} [root]
   */
  function scheduleClamp(root) {
    if (root) pendingRoots.add(root);
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(flushPending);
  }

  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      pendingRoots.clear();
      pendingRoots.add(document);
      scheduleClamp();
    }, RESIZE_DEBOUNCE_MS);
  }

  function onMutations(mutations) {
    let needsClamp = false;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches(SELECTOR) || el.querySelector(SELECTOR)) {
          needsClamp = true;
          break;
        }
      }

      if (needsClamp) break;
    }

    if (!needsClamp) return;

    window.clearTimeout(mutateTimer);
    mutateTimer = window.setTimeout(() => scheduleClamp(), 0);
  }

  /**
   * @param {Event} event
   */
  function onShopifySectionLoad(event) {
    const section = /** @type {CustomEvent} */ (event).target;
    if (section instanceof HTMLElement) {
      scheduleClamp(section);
    }
  }

  function observeDom() {
    if (mutationObserver || !document.body) return;

    mutationObserver = new MutationObserver(onMutations);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function bindEvents() {
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('shopify:section:load', onShopifySectionLoad);
    document.addEventListener('shopify:section:reorder', onShopifySectionLoad);

    // Dawn / OS2 facet drawer & collection AJAX (harmless if never fired)
    document.addEventListener('facet:updated', () => scheduleClamp());
    document.addEventListener('collection:updated', () => scheduleClamp());
  }

  function init() {
    cachedMaxLines = getMaxLines();
    bindEvents();
    observeDom();

    const runInitial = () => scheduleClamp();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runInitial, { once: true });
    } else {
      runInitial();
    }

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(runInitial).catch(() => {});
    }

    window.addEventListener('load', runInitial, { once: true });
  }

  init();
})();